import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { GLOBAL_MESSAGE_HANDLER_TOKEN } from '../service/global-message-handler.token';
import { I18nService } from '../../i18n/i18n.service';
export let AppMessage = /*#__PURE__*/(() => {
  class AppMessage {
    static {
      this.defaults = {
        life: 8000,
        // milliseconds
        closable: true
      };
    }
    get summary() {
      this._hasBeenRead = true;
      return this.getSummary();
    }
    get detail() {
      this._hasBeenRead = true;
      return this._message;
    }
    get message() {
      this._hasBeenRead = true;
      return this._message;
    }
    get severity() {
      return this.getSeverity();
    }
    get status() {
      return this.getStatus();
    }
    get headers() {
      return this.getHeaders();
    }
    get data() {
      return this.getData();
    }
    get handled() {
      return this._handled || false;
    }
    get closable() {
      return typeof this._closable === 'boolean' ? this._closable : this.getDefaults().closable;
    }
    get sticky() {
      return typeof this._sticky === 'boolean' ? this._sticky : false /*this._isError*/;
    } // dont make errors sticky by default anymore.
    // life is just a simple value in this class. app-message-toast will use it to auto-hide message after lifetime ends.
    // in milliseconds
    get life() {
      return typeof this._life === 'number' ? this._life : AppMessage.defaults.life;
    }
    get timestamp() {
      return this._timestamp;
    }
    get isError() {
      return this._isError;
    }
    get isApiError() {
      return this.error instanceof HttpErrorResponse;
    }
    get error() {
      return this.getError();
    }
    get originalError() {
      return this._inputData;
    }
    get duplicateCount() {
      return this.duplicates.length;
    }
    constructor(injector) {
      this.injector = injector;
      this._isError = false;
      // handled is true after a MessageHandlerService received the message
      this._handled = false;
      // allows to specify an explicit message handler to use (for auto-display)
      this._handler = null;
      this._discarded = false;
      // hasBeenRead is being used as internal confirmation - will be true after message.detail has been read
      this._hasBeenRead = false;
      this.duplicates = [];
      this.key = null;
      this._timestamp = Date.now();
      this.startAutoDisplayTimer();
      this.i18n = injector.get(I18nService);
    }
    getDefaults() {
      return AppMessage.defaults;
    }
    setMessage(message) {
      this._inputData = message;
      if (typeof message === 'object') {
        this._setObjectMessage(message);
      } else {
        this._setTextMessage(message);
      }
      return this;
    }
    setError(message) {
      this.setMessage(message);
      this._isError = true;
      return this;
    }
    setSeverity(value) {
      this._severity = value;
      return this;
    }
    setSummary(value) {
      this._summary = value;
      return this;
    }
    // call discard() if you don't want to show the message!
    setHandled(value = true) {
      this._handled = value;
      return this;
    }
    setSticky(to = true) {
      this._sticky = to;
      return this;
    }
    setClosable(to = true) {
      this._closable = to;
      return this;
    }
    // time is in ms!
    setLife(lifeTimeMs = 5000) {
      this._life = lifeTimeMs;
      return this;
    }
    getSeverity() {
      if (typeof this._severity !== 'undefined') {
        return this._severity;
      }
      if (this._isError) {
        const status = this.status;
        if (status === 404 || status >= 500 && status <= 505) {
          return 'error';
        } else {
          return 'warn';
        }
      }
      return this._severity || 'info';
    }
    getStatus() {
      const error = this.getError();
      if (error && typeof error.status === 'number') return error.status;
      if (typeof this._inputData.status === 'number') return this._inputData.status;
      if (typeof this._status === 'number') return this._status;
      return this._isError ? 500 : 200;
    }
    getHeaders() {
      if (this._inputData.headers) return this._inputData.headers;
      return null;
    }
    getData() {
      if (this._inputData instanceof HttpErrorResponse) return this._inputData.error['data']['data'] || this._inputData.error['data'] || undefined;else if (this._inputData instanceof HttpResponse) return this._inputData.body['data'];else if (typeof this._inputData === 'object' && this._inputData['data']) return this._inputData['data'];
      return {};
    }
    getError() {
      if (!this._isError) return false;
      return this._inputData;
    }
    getSummary() {
      if (typeof this._summary === 'string') {
        return this.tryTranslate(this._summary);
      }
      const severity = this.severity;
      if (severity === 'info') return this.i18n.translate('INFO');else if (severity === 'success') return this.i18n.translate('SUCCESS');else if (severity === 'warn') return this.i18n.translate('WARNING');else return this.i18n.translate('ERROR');
    }
    discard() {
      this._discarded = true;
    }
    setHandler(h) {
      this._handler = h;
    }
    // AppMessages can be handled actively (e.g. by discarding them or displaying them inline).
    // If they are not handled actively, this timer will auto-add it to the app-message-toast.
    startAutoDisplayTimer() {
      setTimeout(() => {
        if (this._discarded || this._handled && this._hasBeenRead) return;
        let service = this._handler || this.injector.get(GLOBAL_MESSAGE_HANDLER_TOKEN);
        // console.log('auto display on service', this._handler, this.injector.get(GLOBAL_MESSAGE_HANDLER_TOKEN))
        service.push(this);
      }, 250);
      return this;
    }
    addDuplicates(...d) {
      this.duplicates.push(...d);
    }
    tryTranslate(text, defaultValue) {
      // console.log('tryTranslate',text, this.i18n.maybeTranslate(text))
      return this.i18n.maybeTranslate(text, defaultValue);
      // if(text.startsWith('i18n:')) {
      // }
    }
    _setTextMessage(message) {
      this._error = false;
      this._message = this.tryTranslate(message);
    }
    _setObjectMessage(message) {
      const status = this.getStatus();
      if (message instanceof Error || message instanceof HttpErrorResponse || typeof message.error !== 'undefined' || status && status >= 400) {
        this._isError = true;
        return this._setErrorMessage(message);
      } else {
        // an object that could not be identified as an error.
        this._isError = status && status >= 400 || this.severity === 'error';
        if (this._isError) this._error = message;
        this._message = typeof message.message === 'string' ? this.tryTranslate(message.message) : this.i18n.translate('ERR_MSG_UNKNOWN');
      }
    }
    _setErrorMessage(message) {
      const error = this.getError();
      // errors like HttpErrorResponse can contain an error property containing more information.
      const innerError = error.error || error;
      let errorMessage;
      // Commented lines append requested URL to displayed message text, helpful when debugging E2E
      // setting up a variable (part 1 of 3)
      // let url: string = ''
      if (message instanceof HttpErrorResponse) {
        // HttpErrorResponse is messy. statusText will contain raw custom message and must take highest priority.
        errorMessage = innerError.statusText || innerError.message || this._inputData.message || null;
        // Commented lines append requested URL to displayed message text, helpful when debugging E2E
        // appending info: which url was requested? (part 2 of 3)
        // if(isE2E() && message.url){
        // 	url = ' ' + message.url;
        // }
      } else {
        errorMessage = innerError.message || innerError.statusText || this._inputData.message || null;
      }
      // it seems HttpErrorResponse will generate a statusText if none has been set. 
      // but we dont want to show these texts
      const knownDefaultMessages = ['Http failure response', 'Unknown Error'];
      const isCustomErrorMessage = errorMessage !== null && !knownDefaultMessages.find(text => errorMessage.startsWith(text)) || false;
      const status = this.getStatus();
      if (status === 422) {
        // TODO: should custom errorMessage really be ignored?
        this._message = isCustomErrorMessage ? this.tryTranslate(errorMessage) : this.i18n.translate('ERR_UNPROCESSABLE');
      } else if (status === 401) {
        this._message = isCustomErrorMessage ? this.tryTranslate(errorMessage) : this.i18n.translate('ACCESS_SESSION_INVALID');
      } else if (status === 403) {
        this._message = isCustomErrorMessage ? this.tryTranslate(errorMessage) : this.i18n.translate('ERR_FORBIDDEN2');
      } else if (status === 0) {
        this._summary = this.i18n.translate('ERR_CONNECTION_SHORT');
        this._message = this.i18n.translate('ERR_CONNECTION');
        // Commented lines append requested URL to displayed message text, helpful when debugging E2E
        // make requested url visible on the taken screenshot by concat (part 3 of 3)
        // this._message += url;
      } else {
        if (isCustomErrorMessage) this._message = this.tryTranslate(errorMessage);else this._message = this.i18n.translate('ERR_HTTP', {
          value: status
        });
      }
    }
  }
  return AppMessage;
})();